/**
 * Slash-command tokens anywhere in a composer's text.
 *
 * The convention is the one readers already know from Slack and Notion: a `/`
 * opens the command picker wherever it is typed, not only as the first
 * character. A token is `/name` whose `/` sits at the start of the text or
 * right after whitespace, so `http://x.com/test` and `a/b` stay prose.
 *
 * Two questions the composers ask, in two helpers:
 * - `findTrailingSlashToken`: is the reader typing a token RIGHT NOW (the text
 *   ends in one)? That is when the picker is on screen and filtered by it.
 * - `findKnownSlashToken`: on submit, which token names a command? The first
 *   one on a word boundary that matches the command list, so the rest of the
 *   line can travel as the message. A LEADING token is returned even when it
 *   matches nothing, which keeps the long-standing rule that a line starting
 *   with `/` is a command attempt and never prose.
 */

export interface SlashToken {
  /** The token including its `/`, exactly as typed. */
  name: string
  /** Index of the `/` in the text. */
  start: number
  /** Index just past the last character of the token. */
  end: number
}

const BOUNDARY_TOKEN = /(^|\s)(\/\S*)/g

function boundaryTokens(value: string): Array<SlashToken> {
  const tokens: Array<SlashToken> = []
  for (const match of value.matchAll(BOUNDARY_TOKEN)) {
    const lead = match[1] ?? ''
    const name = match[2] ?? ''
    const start = (match.index ?? 0) + lead.length
    tokens.push({ name, start, end: start + name.length })
  }
  return tokens
}

/**
 * The token the reader is typing: the text ends in a boundary token with no
 * whitespace after it. A bare `/` counts only at the start of the text, where
 * it can mean nothing else; mid-sentence the list waits for a letter, so a
 * slash typed in passing does not throw a picker over the words.
 */
export function findTrailingSlashToken(value: string): SlashToken | null {
  const tokens = boundaryTokens(value)
  const last = tokens[tokens.length - 1]
  if (!last || last.end !== value.length) {
    return null
  }
  if (last.name === '/' && last.start !== 0) {
    return null
  }
  return last
}

/**
 * The token that names a command on submit. The first boundary token whose
 * name matches `commandNames` (case-insensitive, the way the picker filters);
 * failing that, a token at index 0 even when unknown, so a leading `/typo`
 * still reaches the handler and is refused there rather than sent as prose.
 */
export function findKnownSlashToken(
  value: string,
  commandNames: ReadonlyArray<string>,
): SlashToken | null {
  const tokens = boundaryTokens(value)
  if (tokens.length === 0) {
    return null
  }
  const known = new Set(commandNames.map((name) => name.toLowerCase()))
  const match = tokens.find((token) => known.has(token.name.toLowerCase()))
  if (match) {
    return match
  }
  return tokens[0].start === 0 ? tokens[0] : null
}

/** The text without the token, the seam collapsed to one space, trimmed. */
export function stripSlashToken(value: string, token: SlashToken): string {
  const before = value.slice(0, token.start)
  const after = value.slice(token.end)
  return `${before.trimEnd()} ${after.trimStart()}`.trim()
}

/** The text with the token replaced by `replacement` (the picked command). */
export function replaceSlashToken(
  value: string,
  token: SlashToken,
  replacement: string,
): string {
  return `${value.slice(0, token.start)}${replacement}${value.slice(token.end)}`
}
